import * as React from "react";
import {useState, useEffect, Suspense} from "react";
import {createRoot} from "react-dom/client";
import {useTranslation} from "react-i18next";

type FlashSaleTimerParamType = {
  endDateString: string;
  redesignClock?: boolean;
};
type FlashSaleTimerStateType = {
  highTime: string;
  lowTime: string;
};

const FlashSaleTimer: React.FC<FlashSaleTimerParamType> = ({endDateString, redesignClock = true}) => {
  const [state, setState] = useState<FlashSaleTimerStateType>({highTime: "-", lowTime: "-"});
  const {t} = useTranslation();

  useEffect(() => {
    // One-minute shift, because of missing trailing seconds on an elapse
    // of under a minute. Otherwise, offer ends one minute too early
    const date = Date.parse(endDateString) + 60000;

    const intervalHandler = () => {
      const now = Date.now();
      const diff = date - now;

      const diffSeconds = Math.ceil(diff / 1000);
      const minutes = Math.floor(diffSeconds / 60) % 60;
      const hours = Math.floor(diffSeconds / 3600) % 24; // .. / 60s / 60m
      const days = Math.floor(diffSeconds / 86400); // .. / 60s / 60m / 24h

      const tempObject = {
        highTime: "",
        lowTime: "",
      };
      if (days > 0) {
        tempObject.highTime = t("flashsale.days", {count: days});
        tempObject.lowTime = hours > 0 ? t("flashsale.hours", {count: hours}) : "";
      } else if (hours > 0) {
        tempObject.highTime = t("flashsale.hours", {count: hours});
        tempObject.lowTime = minutes > 0 ? t("flashsale.minutes", {count: minutes}) : "";
      } else if (minutes > 0) {
        tempObject.highTime = t("flashsale.minutes", {count: minutes});
        tempObject.lowTime = "";
      } else {
        tempObject.highTime = "";
        tempObject.lowTime = "";
        window.clearInterval(interval);
      }
      setState(tempObject);
    };

    intervalHandler();
    const interval = window.setInterval(intervalHandler, 1000);
  }, []);

  if (state.highTime === "") {
    return t("flashsale.offerEnded");
  }

  return (
    <>
      <span className={`icon icon-clock${redesignClock ? "--white mr-3" : "--lightgrey icon-size--20"}`}>&#160;</span>
      {state.highTime !== "-" && t("flashsale.endsIn--time", {time: `${state.highTime} ${state.lowTime}`})}
    </>
  );
};

export const flashSaleTimers = (anchors: NodeListOf<HTMLAnchorElement>) => {
  Array.from(anchors).forEach((anchor) => {
    const timerDiv = anchor.querySelector<HTMLDivElement>(".flash-sale__timer-text");
    if (!timerDiv || !timerDiv.dataset.endDate || Date.parse(timerDiv.dataset.endDate) < Date.now()) {
      anchor.remove();

      return;
    }
    const root = createRoot(timerDiv);
    root.render(
      <Suspense fallback={<span className="icon icon-clock--lightgrey icon-size--20">&#160;</span>}>
        <FlashSaleTimer endDateString={timerDiv.dataset.endDate ?? ""} redesignClock={false} />
      </Suspense>,
    );
  });
};

export default FlashSaleTimer;
